import React, { useContext, useEffect, useState } from "react";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import JisEmptyList from "../../components/controls/emptylist";
import PdfViewer from "../../components/controls/pdfviewer";
import { api, docLink, fileToBase64, fmtDate, fmtDateTime, toInputDate, tone } from "./hctc_common";
import "./hctc.css";

// My Media Instruments (spec 5), screen media_instruments. Stored in ApplicantMediaInstrument (not ProfileInstrument).
const isImage = (u) => /\.(png|jpe?g)$/i.test(u || "");

export default function MediaInstruments() {
  const { token, setIsExpired } = useContext(ShareContext);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [archived, setArchived] = useState(0);
  const [tab, setTab] = useState("all");
  const [edit, setEdit] = useState(null);
  const [usage, setUsage] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    const d = await api(token, setIsExpired, "mediainstrument/mine");
    setLoading(false);
    if (d.code === 0) {
      setItems(d.items || []);
      setTypes(d.types || []);
      setArchived(d.archived || 0);
    } else if (d.code !== -10) setErr(d.status || "Unable to load your instruments");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (i, action) => {
    setErr("");
    setMsg("");
    setLoading(true);
    const d = await api(token, setIsExpired, `mediainstrument/action/${i.id}`, { method: "POST", body: { action } });
    setLoading(false);
    if (d.code === 0) {
      setMsg(d.deleted ? "Instrument deleted" : action === "delete" ? "Instrument archived (it was used in an application)" : "Updated");
      load();
    } else if (d.code !== -10) setErr(d.status || "The action failed");
  };

  const openUsage = async (i) => {
    setLoading(true);
    const d = await api(token, setIsExpired, `mediainstrument/usage${i ? `?instrumentId=${i.id}` : ""}`);
    setLoading(false);
    if (d.code === 0) setUsage({ ...d, title: i ? i.instrumentName : "All instruments" });
  };

  const shown = items.filter((i) => tab === "all" || i.instrumentType === tab);

  return (
    <div className="hctc-page">
      <div className="hctc-toolbar">
        <div className="left">
          <div>
            <h2>My Media Instruments</h2>
            <div className="hctc-sub">
              Signatures, seals and stamps you can apply to your applications. Only active, non-expired instruments can be selected. NBA, notary, commissioner and other
              regulated instruments are verified before activation.
            </div>
          </div>
        </div>
        <div className="right">
          <JisButton variant="outline" onClick={() => openUsage(null)}>
            Instrument usage history
          </JisButton>
          <JisButton onClick={() => setEdit({ id: 0, instrumentType: tab !== "all" ? tab : "signature" })}>Upload instrument</JisButton>
        </div>
      </div>
      <div className="hctc-chips">
        <button className={`hctc-chip ${tab === "all" ? "active" : ""}`} onClick={() => setTab("all")}>
          All ({items.length})
        </button>
        {types.map((t) => (
          <button key={t.code} className={`hctc-chip ${tab === t.code ? "active" : ""}`} onClick={() => setTab(t.code)}>
            {t.title} ({items.filter((i) => i.instrumentType === t.code).length})
          </button>
        ))}
      </div>
      {msg && <div className="hctc-ok">{msg}</div>}
      {err && <div className="hctc-err">{err}</div>}

      <div className="hctc-grid wide">
        {shown.map((i) => (
          <div key={i.id} className="hctc-card">
            <div style={{ display: "flex", gap: 12 }}>
              {isImage(i.url) ? (
                <img className="hctc-thumb" style={{ width: 120, height: 90, cursor: "pointer" }} src={docLink(i.url)} alt={i.instrumentName} onClick={() => setPreview(docLink(i.url))} />
              ) : (
                <div className="hctc-thumb" style={{ width: 120, height: 90, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={() => setPreview(docLink(i.url))}>
                  PDF
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>
                  {i.instrumentName} {i.isDefault && <span className="hctc-pill info">Default</span>}
                </div>
                <div className="hctc-muted">{i.typeTitle}</div>
                <div>
                  <span className={`hctc-pill ${tone(i.status)}`}>{i.status}</span>
                  {i.verificationStatus !== "Not Required" && <span className={`hctc-pill ${tone(i.verificationStatus)}`}>{i.verificationStatus}</span>}
                </div>
                <div className="hctc-muted">
                  {i.instrumentNumber && <div>No. {i.instrumentNumber}</div>}
                  {i.issuingAuthority && <div>{i.issuingAuthority}</div>}
                  {(i.effectiveDate || i.expiryDate) && (
                    <div>
                      {i.effectiveDate ? fmtDate(i.effectiveDate) : "-"} to {i.expiryDate ? fmtDate(i.expiryDate) : "no expiry"}
                    </div>
                  )}
                  {i.statusReason && <div>Note: {i.statusReason}</div>}
                  <div>Used in {i.usage} application(s)</div>
                </div>
              </div>
            </div>
            <div className="hctc-row-actions" style={{ marginTop: 10 }}>
              <JisButton variant="plain" onClick={() => setEdit(i)}>
                Edit / replace
              </JisButton>
              {i.status === "Active" && !i.isDefault && (
                <JisButton variant="plain" onClick={() => act(i, "set_default")}>
                  Set default
                </JisButton>
              )}
              {i.isDefault && (
                <JisButton variant="plain" onClick={() => act(i, "remove_default")}>
                  Remove default
                </JisButton>
              )}
              {i.status === "Active" && (
                <JisButton variant="plain" onClick={() => act(i, "deactivate")}>
                  Deactivate
                </JisButton>
              )}
              {i.status === "Inactive" && (
                <JisButton variant="plain" onClick={() => act(i, "activate")}>
                  Activate
                </JisButton>
              )}
              {["Draft", "Rejected"].includes(i.status) && (
                <JisButton variant="plain" onClick={() => act(i, "request_verification")}>
                  Request verification
                </JisButton>
              )}
              <JisButton variant="plain" onClick={() => openUsage(i)}>
                Usage
              </JisButton>
              <JisButton variant="plain" onClick={() => act(i, "delete")}>
                {i.usage > 0 ? "Archive" : "Delete"}
              </JisButton>
            </div>
          </div>
        ))}
      </div>
      {!loading && shown.length === 0 && <JisEmptyList title="No instruments here yet" />}
      {archived > 0 && <div className="hctc-muted">{archived} archived instrument(s) are kept because they were used in applications.</div>}

      {edit && <InstrumentDialog item={edit} types={types} token={token} setIsExpired={setIsExpired} onClose={() => setEdit(null)} onSaved={(m) => { setEdit(null); setMsg(m); load(); }} />}
      {usage && (
        <JisDialogForm title={`Usage: ${usage.title}`} onClose={() => setUsage(null)} minWidth="50%">
          <div className="hctc-dialog-body">
            <h4>Applied to applications</h4>
            {usage.items.length === 0 ? (
              <div className="hctc-muted">Not used yet</div>
            ) : (
              <table className="case-table">
                <tbody>
                  {usage.items.map((u) => (
                    <tr key={u.id}>
                      <td>{u.instrumentName}</td>
                      <td>
                        <a href={`/service-applications/${u.application?.id}`}>{u.application?.applicationNumber}</a>
                      </td>
                      <td>{u.application?.status}</td>
                      <td>{fmtDateTime(u.appliedDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <h4 style={{ marginTop: 14 }}>History</h4>
            <ul className="hctc-timeline">
              {usage.history.map((h) => (
                <li key={h.id}>
                  <b>{h.action}</b> {h.newStatus && h.previousStatus !== h.newStatus && <span className="hctc-muted">{h.previousStatus || "-"} → {h.newStatus}</span>}
                  {h.comments && <div>{h.comments}</div>}
                  <div className="when">{fmtDateTime(h.createdOn)}</div>
                </li>
              ))}
            </ul>
          </div>
        </JisDialogForm>
      )}
      {preview && (
        <JisDialogForm title="Instrument" onClose={() => setPreview("")}>
          <div style={{ height: "70vh", minWidth: "min(700px, 85vw)" }}>
            {isImage(preview) ? <img src={preview} alt="instrument" style={{ maxWidth: "100%", maxHeight: "100%", display: "block", margin: "auto", background: "#fff" }} /> : <PdfViewer pdfUrl={preview} />}
          </div>
        </JisDialogForm>
      )}
      <BackLoader open={loading} />
    </div>
  );
}

function InstrumentDialog({ item, types, token, setIsExpired, onClose, onSaved }) {
  const [f, setF] = useState({
    id: item.id || 0,
    instrumentType: item.instrumentType || "signature",
    instrumentName: item.instrumentName || "",
    instrumentNumber: item.instrumentNumber || "",
    issuingAuthority: item.issuingAuthority || "",
    branch: item.branch || "",
    effectiveDate: toInputDate(item.effectiveDate),
    expiryDate: toInputDate(item.expiryDate),
    description: item.description || "",
    isDefault: !!item.isDefault,
  });
  const [file, setFile] = useState(null);
  const [support, setSupport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const t = types.find((x) => x.code === f.instrumentType);
  const imageOnly = ["signature", "watermark", "logo"].includes(f.instrumentType);
  const on = (e) => setF({ ...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    setErr("");
    if (file && file.size > 5 * 1024 * 1024) {
      setErr("The instrument file must be 5 MB or less.");
      return;
    }
    setBusy(true);
    const body = {
      ...f,
      effectiveDate: f.effectiveDate || null,
      expiryDate: f.expiryDate || null,
      base64: file ? await fileToBase64(file) : null,
      fileName: file?.name || null,
      supportBase64: support ? await fileToBase64(support) : null,
      supportFileName: support?.name || null,
    };
    const d = await api(token, setIsExpired, "mediainstrument/save", { method: "POST", body });
    setBusy(false);
    if (d.code === 0) onSaved(d.status === "Pending Verification" ? "Saved. The instrument will be active once it is verified." : "Saved");
    else if (d.code !== -10) setErr(d.status || "Unable to save");
  };

  return (
    <JisDialogForm title={item.id ? "Edit instrument" : "Upload instrument"} onClose={onClose} minWidth="45%">
      <div className="hctc-dialog-body">
        {err && <div className="hctc-err">{err}</div>}
        <div className="hctc-grid">
          <div className="hctc-field">
            <label>
              Instrument type<span className="req">*</span>
            </label>
            <select name="instrumentType" value={f.instrumentType} onChange={on} disabled={!!item.id}>
              {types.map((x) => (
                <option key={x.code} value={x.code}>
                  {x.title}
                </option>
              ))}
            </select>
            {t && <div className="hint">For: {t.eligible}. Placement: {t.placement}.</div>}
          </div>
          <div className="hctc-field span2">
            <label>
              Instrument name<span className="req">*</span>
            </label>
            <input name="instrumentName" value={f.instrumentName} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>
              Instrument number{t?.needsNumber && <span className="req">*</span>}
            </label>
            <input name="instrumentNumber" value={f.instrumentNumber} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>
              Issuing authority{t?.needsNumber && <span className="req">*</span>}
            </label>
            <input name="issuingAuthority" value={f.issuingAuthority} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>Branch / division</label>
            <input name="branch" value={f.branch} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>Effective date</label>
            <input type="date" name="effectiveDate" value={f.effectiveDate} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>
              Expiry date{t?.needsExpiry && <span className="req">*</span>}
            </label>
            <input type="date" name="expiryDate" value={f.expiryDate} onChange={on} />
          </div>
          <div className="hctc-field full">
            <label>
              Instrument file ({imageOnly ? "PNG or JPG" : "PNG, JPG or PDF"}, max 5 MB){!item.id && <span className="req">*</span>}
            </label>
            <input type="file" accept={imageOnly ? ".png,.jpg,.jpeg" : ".png,.jpg,.jpeg,.pdf"} onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div className="hint">Use a transparent PNG for signatures and stamps, at least 300 DPI. {item.id && "Leave empty to keep the current file."}</div>
          </div>
          {t?.needsVerification && (
            <div className="hctc-field full">
              <label>
                Supporting document (PDF){f.instrumentType !== "corporate_seal" && !item.supportUrl && <span className="req">*</span>}
              </label>
              <input type="file" accept=".pdf" onChange={(e) => setSupport(e.target.files?.[0] || null)} />
              {item.supportUrl && (
                <div className="hint">
                  <a href={docLink(item.supportUrl)} target="_blank" rel="noreferrer">
                    Current supporting document
                  </a>
                </div>
              )}
            </div>
          )}
          <div className="hctc-field full">
            <label>Description</label>
            <textarea name="description" value={f.description} onChange={on} />
          </div>
          <div className="hctc-field">
            <label className="hctc-check">
              <input type="checkbox" name="isDefault" checked={f.isDefault} onChange={on} /> Set as default
            </label>
          </div>
        </div>
        {t?.needsVerification && <div className="hctc-info">This instrument type is verified by the court before it can be used.</div>}
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={save} loading={busy} disabled={!f.instrumentName || (!item.id && !file)}>
            Save
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}
